<template>
  <span
    v-if="hasContent"
    class="section-platform-tags"
  >
    <AntTag
      v-if="display.mode === 'all' && showAllTag"
      class="section-platform-tags--all"
    >
      ALL
    </AntTag>

    <span
      v-else-if="display.mode === 'whitelist' || display.mode === 'exclude'"
      class="section-platform-tags-group"
    >
      <AntTooltip
        v-if="visibleIds.length"
        :title="modeTooltipTitle"
      >
        <span class="section-platform-tags-visible">
          <AntTag
            v-for="platformId in visibleIds"
            :key="`${display.mode}-${platformId}`"
            :color="tagColor"
            class="section-platform-tag"
          >
            {{ tagPrefix }}{{ platformLabel(platformId) }}
          </AntTag>
        </span>
      </AntTooltip>

      <AntPopover
        v-if="overflowCount > 0"
        trigger="hover"
        placement="topLeft"
        overlayClassName="section-platform-tags-popover-overlay"
      >
        <AntTag class="section-platform-tag section-platform-tag--more">
          +{{ overflowCount }}
        </AntTag>
        <template #content>
          <div class="section-platform-tags-overflow">
            <AntTag
              v-for="platformId in overflowIds"
              :key="`${display.mode}-o-${platformId}`"
              :color="tagColor"
              class="section-platform-tag"
            >
              {{ tagPrefix }}{{ platformLabel(platformId) }}
            </AntTag>
          </div>
        </template>
      </AntPopover>
    </span>
  </span>
</template>

<script>
import { computed } from 'vue'

import {
  Tag as AntTag,
  Tooltip as AntTooltip,
  Popover as AntPopover,
} from 'ant-design-vue'

export default {
  name: 'SectionPlatformTags',
  components: {
    AntTag,
    AntTooltip,
    AntPopover,
  },
  props: {
    section: {
      type: Object,
      default: () => ({}),
    },
    platformsMap: {
      type: Object,
      required: true,
    },
    showAllTag: {
      type: Boolean,
      default: false,
    },
    /** 樹上直接顯示的 platform tag 數量上限，其餘以 +N Popover 顯示 */
    tagLimit: {
      type: Number,
      default: 5,
    },
  },
  setup(props) {
    const getSectionPlatformDisplay = (section = {}) => {
      const platforms = section.platforms || []
      const excludedPlatforms = section.excludedPlatforms || []
      if (platforms.length) {
        return { mode: 'whitelist', platformIds: platforms }
      }
      if (excludedPlatforms.length) {
        return { mode: 'exclude', platformIds: excludedPlatforms }
      }
      return { mode: 'all', platformIds: [] }
    }

    const slicePlatformIdsForTags = (platformIds, limit) => {
      const ids = platformIds || []
      if (ids.length <= limit) {
        return { visibleIds: ids, overflowIds: [], overflowCount: 0 }
      }
      return {
        visibleIds: ids.slice(0, limit),
        overflowIds: ids.slice(limit),
        overflowCount: ids.length - limit,
      }
    }

    const display = computed(() => getSectionPlatformDisplay(props.section))

    const slicedTags = computed(() => slicePlatformIdsForTags(
      display.value.platformIds,
      props.tagLimit,
    ))
    const visibleIds = computed(() => slicedTags.value.visibleIds)
    const overflowIds = computed(() => slicedTags.value.overflowIds)
    const overflowCount = computed(() => slicedTags.value.overflowCount)

    const modeTooltipTitle = computed(() => {
      if (props.section.platformsFromDataTable) {
        return '區塊 Data Table 的平台'
      }
      return display.value.mode === 'exclude' ? 'Section 排除平台' : 'Section 支援平台'
    })

    const tagColor = computed(() => (display.value.mode === 'exclude' ? 'red' : 'green'))

    const tagPrefix = computed(() => (display.value.mode === 'exclude' ? '排除 ' : ''))

    const hasContent = computed(() => (
      (props.showAllTag && display.value.mode === 'all')
      || display.value.mode !== 'all'
    ))

    const platformLabel = platformId => props.platformsMap[platformId] || platformId

    return {
      display,
      visibleIds,
      overflowIds,
      overflowCount,
      modeTooltipTitle,
      tagColor,
      tagPrefix,
      hasContent,
      platformLabel,
    }
  },
}
</script>

<style scoped>
.section-platform-tags {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-left: 2px;
  max-width: 100%;
}

.section-platform-tags-group {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  max-width: 100%;
}

.section-platform-tags-visible {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  max-width: 100%;
}

:deep(.section-platform-tags-visible) {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.section-platform-tag {
  margin: 0;
}

.section-platform-tags--all {
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  background: #fafafa;
  border-color: #d9d9d9;
}

.section-platform-tag--more {
  cursor: pointer;
}

.section-platform-tags-overflow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 320px;
}

</style>

<style>
/* Popover 需高於 Tooltip，且勿被樹節點 overflow 裁切 */
.section-platform-tags-popover-overlay {
  z-index: 1100 !important;
}
</style>
